import { toast } from "react-toastify";
import {
  createOrder,
  createSubscription,
  verifyPayment,
  verifySubscription,
} from "../apis/subscription";
import { loadScript, openRazorpayCheckout } from "./razorpay";
import { isTrialPlan, checkoutPeriod, planBase } from "./planPricing";
import { resolveCheckoutBrand } from "./checkoutBrand";

const AUTOPAY_ON = new Set(["created", "authenticated", "active", "pending"]);

export function isAutopayOn(subscription) {
  return Boolean(subscription && AUTOPAY_ON.has(subscription.autopay_status));
}

export function fmtChargeDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * Platform plan checkout. Trial stays one-time; paid plans create a Razorpay mandate.
 */
export async function startPlatformCheckout({
  plan,
  billingPeriod = "monthly",
  enableExisting = false,
  themeColor = "#16A34A",
  checkoutName,
  checkoutImage,
  onSuccess,
  onSettled,
}) {
  try {
    const brand = resolveCheckoutBrand();
    const name = checkoutName || brand.name;
    const image = checkoutImage || brand.image;
    if (isTrialPlan(plan) && planBase(plan, checkoutPeriod(plan, billingPeriod)) <= 0) {
      const orderResponse = await createOrder(plan.plan_name, checkoutPeriod(plan, billingPeriod));
      if (!orderResponse.data.success) {
        throw new Error(orderResponse.data.error || "Failed to start the free plan");
      }
      const data = orderResponse.data.data;
      toast.success(data.message || "Free plan activated");
      if (onSuccess) onSuccess(data.subscription, data);
      if (onSettled) onSettled();
      return;
    }
    await loadScript();
    if (isTrialPlan(plan)) {
      const orderResponse = await createOrder(plan.plan_name, checkoutPeriod(plan, billingPeriod));
      if (!orderResponse.data.success) {
        throw new Error(orderResponse.data.error || "Failed to create order");
      }
      const data = orderResponse.data.data;
      openRazorpayCheckout({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency || "INR",
        name,
        image,
        description: `Subscribe to ${plan.plan_display_name}`,
        orderId: data.order_id,
        themeColor,
        onDismiss: onSettled,
        handler: async (response) => {
          try {
            const verifyResponse = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (!verifyResponse.data.success) {
              throw new Error(verifyResponse.data.error || "Payment verification failed");
            }
            toast.success("Subscription activated successfully!");
            if (onSuccess) onSuccess(verifyResponse.data.data.subscription, verifyResponse.data.data);
          } catch (error) {
            toast.error(error.response?.data?.error || error.message || "Payment verification failed");
          } finally {
            if (onSettled) onSettled();
          }
        },
      });
      return;
    }

    const subResponse = await createSubscription(plan.plan_name, billingPeriod, enableExisting);
    if (!subResponse.data.success) {
      throw new Error(subResponse.data.error || "Failed to start auto-pay");
    }
    const data = subResponse.data.data;
    openRazorpayCheckout({
      key: data.key_id,
      amount: data.amount,
      currency: data.currency || "INR",
      name,
      image,
      description: enableExisting
        ? `Enable auto-pay for ${plan.plan_display_name}`
        : `Subscribe to ${plan.plan_display_name} (auto-pay)`,
      subscriptionId: data.subscription_id,
      themeColor,
      onDismiss: onSettled,
      handler: async (response) => {
        try {
          const verifyResponse = await verifySubscription({
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_subscription_id: response.razorpay_subscription_id,
            razorpay_signature: response.razorpay_signature,
          });
          if (!verifyResponse.data.success) {
            throw new Error(verifyResponse.data.error || "Payment verification failed");
          }
          toast.success(verifyResponse.data.data.message || "Auto-pay activated");
          if (onSuccess) onSuccess(verifyResponse.data.data.subscription, verifyResponse.data.data);
        } catch (error) {
          toast.error(error.response?.data?.error || error.message || "Payment verification failed");
        } finally {
          if (onSettled) onSettled();
        }
      },
    });
  } catch (error) {
    toast.error(error.response?.data?.error || error.message || "Failed to initiate payment");
    if (onSettled) onSettled();
  }
}
